import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  Icon,
  InlineMessage,
  InputField,
  Loader,
} from '@saviynt/design-system';

import { Label, useInvertedLocalize } from '../../../../utilities';
import { type ErrorMessage } from '../../models';
import { invertedMessages } from '../messages';

import { type OtpFormProps } from './types';

type InnerOtpFormProps = OtpFormProps & {
  /** Fired when the form is selected - use to initiate any necessary backend
   * processes */
  onMount?: () => Promise<void>;

  /** Whether the factor type has completed any initial processing, such as
   * sending a code */
  isReadyToVerify?: boolean;

  /** Fired when user presses Enter on the form */
  onVerify: (code: string) => Promise<boolean | ErrorMessage>;
};

/** Displays a generic OTP form and manages user interactions and feedback, and
 * generic submission logic */
export default function OtpForm({
  onMount,
  isReadyToVerify = true,
  isCodeReadyForSubmission,
  onVerify,
  onSuccess,
  onFailure,
  onSubmittableChange,
}: InnerOtpFormProps): JSX.Element {
  const [code, setCode] = useState('');
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const l = useInvertedLocalize(invertedMessages);

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter' && isCodeValid) {
      await verifyCode(
        code,
        onSuccess,
        onFailure,
        onVerify,
        setIsVerifying,
        setHasError,
        onSubmittableChange
      );
    }
  };

  const setValue = (value: string) => {
    const isValid = value.length === 6;

    setCode(value);
    setIsCodeValid(isValid);
    onSubmittableChange(isValid);
  };

  useNotifyOnMount(onSubmittableChange, isCodeValid, onMount);
  useCodeVerificationWhenRequested(
    isCodeReadyForSubmission,
    isVerifying,
    onVerify,
    code,
    setIsVerifying,
    setHasError,
    onSuccess,
    onFailure,
    onSubmittableChange
  );

  if (!isReadyToVerify) {
    return <Loader kind='loop' format='main' color='brand' />;
  }

  return (
    <InputField
      name='otpCode'
      placeholder={l('Enter the 6-digit code')}
      label={<Label htmlFor='Digit Code'>{l('6-Digit Code')}</Label>}
      value={code}
      setValue={setValue}
      maxLength={6}
      inputmode='numeric'
      isDisabled={isVerifying}
      onKeyDown={handleKeyDown}
      CriticalHelperText={
        <InlineMessage
          text={l('Please enter a valid code')}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      }
      isCritical={hasError}
    />
  );
}

function useCodeVerificationWhenRequested(
  isCodeReadyForSubmission: boolean,
  isVerifying: boolean,
  onVerify: (code: string) => Promise<boolean | ErrorMessage>,
  code: string,
  setIsVerifying: Dispatch<SetStateAction<boolean>>,
  setHasError: Dispatch<SetStateAction<boolean>>,
  onSuccess: () => void,
  onFailure: () => void,
  onSubmittableChange: (state: boolean) => void
) {
  useEffect(() => {
    if (isCodeReadyForSubmission && !isVerifying) {
      verifyCode(
        code,
        onSuccess,
        onFailure,
        onVerify,
        setIsVerifying,
        setHasError,
        onSubmittableChange
      ).catch(() => {
        setHasError(true);
      });
    }
  }, [
    isCodeReadyForSubmission,
    isVerifying,
    onVerify,
    code,
    setIsVerifying,
    setHasError,
    onSuccess,
    onFailure,
    onSubmittableChange,
  ]);
}

function useNotifyOnMount(
  onSubmittableChange: (state: boolean) => void,
  isCodeValid: boolean,
  onMount?: () => void
) {
  useEffect(() => {
    if (onMount) {
      onMount();
    }

    onSubmittableChange(isCodeValid);
  }, [isCodeValid, onSubmittableChange, onMount]);
}

async function verifyCode(
  code: string,
  onSuccess: () => void,
  onFailure: () => void,
  onVerify: (code: string) => Promise<boolean | ErrorMessage>,
  setIsVerifying: Dispatch<SetStateAction<boolean>>,
  setHasError: Dispatch<SetStateAction<boolean>>,
  onSubmittableChange: (state: boolean) => void
) {
  setHasError(false);
  setIsVerifying(true);
  onSubmittableChange(false);

  try {
    const isVerified = await onVerify(code);

    if (isVerified === true) {
      onSuccess();

      return;
    }

    // isVerified === false || typeof isVerified === 'string':
    setHasError(true);
    onSubmittableChange(true);
    onFailure();
  } catch {
    setHasError(true);
    onSubmittableChange(true);
    onFailure();
  } finally {
    setIsVerifying(false);
  }
}
